import { useForm } from '@tanstack/react-form';
import { toast } from 'react-toastify';
import {
  Button,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useCreateCodeMutation } from 'src/api/codes/createCode';
import { useUpdateCodeMutation } from 'src/api/codes/updateCode';
import { Code } from 'src/api/codes/getCodes';
import { UploadImage } from './UploadImage';

const defaultState = {
  code: '',
  name: '',
  logo: '',
  ordering: 99,
  status: 'active',
} as unknown as Code;

interface Props {
  open: boolean;
  onClose: () => void;
  code?: Code;
}

export const CodeDialog = ({ onClose, ...props }: Props) => {
  const form = useForm({
    defaultValues: props?.code || defaultState,
    onSubmit: async ({ value }) => {
      await saveCode(value);
    },
    validators: {
      onSubmitAsync: async ({ value }) => {
        if (!value.logo) return toast.error('Logo is required');
        if (!value.code.trim()) return toast.error('Code is required');
        if (!value.name.trim()) return toast.error('Name is required');
        if (value.code.trim().length < 2)
          return toast.error('Code must be at least 2 characters long');
        if (value.name.trim().length < 2)
          return toast.error('Name must be at least 2 characters long');
        return null;
      },
    },
  });

  // * API
  const { mutateAsync: createCode, isPending: isCreatePending } =
    useCreateCodeMutation();
  const { mutateAsync: updateCode, isPending: isUpdatePending } =
    useUpdateCodeMutation();

  const saveCode = async (code: Code) => {
    try {
      if (code.id) {
        await updateCode(code);
      } else {
        await createCode(code);
      }

      form.reset();
      onClose();
    } catch {
      toast.error('Failed to save game');
    }
  };

  const onCancel = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog
      open={props.open}
      onClose={onClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
    >
      <DialogTitle id='alert-dialog-title'>
        {props.code?.id ? 'Edit' : 'Create'} Code
      </DialogTitle>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <DialogContent>
          <Grid container gap={2} xs={12}>
            <Grid container gap={2} xs={12}>
              {/* Code */}
              <Grid item xs={3}>
                <form.Field name='code'>
                  {({ state, handleChange }) => {
                    return (
                      <>
                        <TextField
                          id='code'
                          label='Code'
                          required
                          disabled={Boolean(props.code?.id)}
                          value={state.value}
                          onChange={(e) => handleChange(e.target.value)}
                        />
                      </>
                    );
                  }}
                </form.Field>
              </Grid>
              {/* Name */}
              <Grid sx={{ width: '100%' }} xs={6}>
                <form.Field name='name'>
                  {({ state, handleChange }) => (
                    <TextField
                      id='name'
                      label='Name'
                      required
                      value={state.value}
                      onChange={(e) => handleChange(e.target.value)}
                      sx={{ width: '100%' }}
                    />
                  )}
                </form.Field>
              </Grid>
            </Grid>

            <Grid container xs={12} gap={2}>
              {/* Status */}
              <Grid item xs={3}>
                <form.Field name='status'>
                  {({ state, handleChange }) => (
                    <FormControl sx={{ width: '100%', minWidth: 120 }}>
                      <InputLabel id='status'>Status</InputLabel>
                      <Select
                        label='status'
                        id='status'
                        defaultValue='active'
                        value={state.value}
                        onChange={(e) => handleChange(e.target.value)}
                      >
                        <MenuItem value='active'>Active</MenuItem>
                        <MenuItem value='inactive'>Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </form.Field>
              </Grid>
              {/* Order */}
              <Grid item xs={3}>
                <form.Field name='ordering'>
                  {({ state, handleChange }) => (
                    <TextField
                      id='ordering'
                      label='Ordering'
                      type='number'
                      value={state.value}
                      onChange={(e) => handleChange(Number(e.target.value))}
                    />
                  )}
                </form.Field>
              </Grid>
            </Grid>

            <Grid container gap={2} sx={{ mt: 2 }}>
              {/* Upload Image */}
              <form.Field
                name='logo'
                children={(props) => (
                  <UploadImage {...props} label='Logo' required />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            type='submit'
            autoFocus
            disabled={isCreatePending || isUpdatePending}
          >
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
